import { ENTITLEMENT } from '@/components/constants/entitlements';
import { ExperienceFeedbackNudge } from '@/components/experience-feedback/ExperienceFeedbackNudge';
import {
  creditAllowanceRefusalAtom,
  rememberCreditAllowanceRefusal,
} from '@/components/ui/agent-credit-allowance-banner/creditAllowanceRefusalAtom';
import { mergeCreditAllowanceGhost } from '@/components/ui/agent-credit-allowance-banner/mergeCreditAllowanceGhost';
import { useAgentCreditAllowanceInChatChrome } from '@/components/ui/agent-credit-allowance-banner/useAgentCreditAllowanceInChatChrome';
import { copilotRunContextByProjectAtom } from '@/features/copilot/copilot-run-sessions';
import {
  useIsProjectsContextualLoaderPhrasesEnabled,
  useIsWorkLetterArtifactEnabled,
  useIsWorkSessionArtifactV2Enabled,
} from '@/features/flags';
import { useIsDesktopTargetSelectorEnabled } from '@/features/flags/desktop-target-selector-flag';
import { CloudComputerLiveView } from '@/features/rpa/cua-cloud/CloudComputerLiveView';
import {
  classifyCuaCloudProviderState,
  CUA_CLOUD_MACHINE_QUERY_KEY,
  cuaCloudClient,
} from '@/features/rpa/cua-cloud/cuaCloud';
import { useDesktopPermissionGate } from '@/features/rpa/desktop-permission-gate';
import { useRoutineForProject } from '@/features/workflows/hooks';
import { useIsEntitlementEnabled } from '@/hooks/entitlements/useIsEntitlementEnabled';
import {
  trackMobileHandoffCardActioned,
  trackProjectChatErrorOnce,
  trackWorkExperienceFeedbackActioned,
} from '@/models/analytics/features/work';
import { rpaBridge } from '@/tauri/rpa';
import { resolveExportDocumentFilename } from '@/utils/fileHelpers';
import { isProductFeedbackAvailable } from '@/utils/heidiReach';
import {
  DesktopTargetViewSelectionProvider,
  ProjectChatContent,
  useDesktopTargetViewSelection,
  type ProjectChatHost,
  type ProjectChatProps as ProjectChatViewProps,
  type ProjectChatTurnControls as ProjectChatViewTurnControls,
  type ProvisionalSubmitOutcome as ProvisionalSubmitViewOutcome,
  type YourResidentDrawerHost,
} from '@heidi/agents';
import { resolveCreatedTitle } from '@heidi/agents/data';
import { isGetMobileAppModalOpenAtom } from '@heidi/growth';
import { useQueryPatientProfileById } from '@heidi/patients/data';
import { invalidateSessionsQuery } from '@heidi/session/data';
import { useQuery } from '@tanstack/react-query';
import { getDefaultStore, useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { useCallback, useMemo } from 'react';
import { artifactFromToolUse } from '../harness/artifactFromTool';
import {
  registerProjectBackground,
  unregisterProjectBackground,
} from '../hooks/projectsBackgroundRegistry';
import { useIngestComputerUseNotePushCandidates } from '../hooks/useIngestComputerUseNotePushCandidates';
import { useProjectArtifactAutoOpen } from '../hooks/useProjectArtifactAutoOpen';
import { useProjectDesktopDelegation } from '../hooks/useProjectDesktopDelegation';
import { usePublishNotePushApply } from '../hooks/usePublishNotePushApply';
import { trackWorkRpaDesktopRequiredShown } from '../lib/track-work-rpa-desktop-required-shown';
import { readStandardExportPayload } from '../timeline/evidence-tool-adapters/shared/readers';
import { AssistantText } from '../timeline/projectTimelineBlocks';
import { useProjectTransport } from '../transport/useProjectTransport';
import { ProjectChatComposer } from './ProjectChatComposer';
import { ProjectConversationTimeline } from './ProjectConversationTimeline';
import { ProjectMessageActions } from './ProjectMessageActions';
import { ProjectReplyMessageActions } from './ProjectReplyMessageActions';

/**
 * Session's own list-invalidation command, sequenced here rather than reached
 * for inside `useProjectArtifactAutoOpen` (delivery §7 — a foreign domain's
 * command is composed in the consuming UI). Module scope so the port has a
 * stable identity: the hook lists it as an effect dependency.
 */
const invalidateSessionsList = (): void => void invalidateSessionsQuery();

// The resident drawer's app half: the desktop-target selector verdict it ships
// behind, the cloud machine, polled while that verdict is on and classified here,
// and its live screen. Module scope, so each hook keeps one identity.
const RESIDENT_DRAWER_HOST: YourResidentDrawerHost = {
  useIsEnabled: () => useIsDesktopTargetSelectorEnabled(),
  useResidentMachine: (enabled) => {
    const machineQuery = useQuery({
      queryKey: CUA_CLOUD_MACHINE_QUERY_KEY,
      queryFn: async () => (await cuaCloudClient.getMachineDetails()).data,
      enabled,
      retry: false,
      refetchInterval: 30_000,
    });
    const machine = machineQuery.data;
    if (!machine) return undefined;
    const kind = classifyCuaCloudProviderState(machine.provider_state);
    return {
      displayName: machine.display_name,
      state: kind === 'running' || kind === 'paused' ? kind : 'starting',
    };
  },
  renderLiveView: () => <CloudComputerLiveView className="aspect-video" />,
};

const CREATED_ARTIFACT_PORTS: ProjectChatHost['createdArtifactPorts'] = {
  resolveArtifact: artifactFromToolUse,
  resolveObjectTitle: resolveCreatedTitle,
  readExportPayload: readStandardExportPayload,
  resolveExportFilename: resolveExportDocumentFilename,
};

// What the Work chat reads from the app. Module scope, so each member keeps one
// identity; the chat calls every hook where it always called it, in the same
// order, and reads the plain members where it always read them.
const PROJECT_CHAT_HOST: ProjectChatHost = {
  useRoutineForProject: (projectId) => useRoutineForProject(projectId),
  useCreditAllowanceChrome: (options) =>
    useAgentCreditAllowanceInChatChrome(true, options),
  useProjectTransport: (projectId, options) => useProjectTransport(projectId, options),
  useDesktopPermissionGate: () => useDesktopPermissionGate(),
  useIsProjectsContextualLoaderPhrasesEnabled: () =>
    useIsProjectsContextualLoaderPhrasesEnabled(),
  useIsWorkLetterArtifactEnabled: () => useIsWorkLetterArtifactEnabled(),
  useLetterFlowEntitlement: () => useIsEntitlementEnabled(ENTITLEMENT.LETTER_FLOW),
  useQueryPatientProfileById: (patientProfileId, options) =>
    useQueryPatientProfileById(patientProfileId, options),
  useRequiresRedactions: (projectId) =>
    useAtomValue(
      useMemo(
        () =>
          selectAtom(copilotRunContextByProjectAtom, (byProject) =>
            Boolean(byProject[projectId]?.patientName)
          ),
        [projectId]
      )
    ),
  useProjectDesktopDelegation: (args) => useProjectDesktopDelegation(args),
  useCreditAllowanceGhostItems: (items, projectId) => {
    const creditAllowanceRefusal = useAtomValue(creditAllowanceRefusalAtom, {
      store: getDefaultStore(),
    });
    return useMemo(
      () =>
        projectId
          ? mergeCreditAllowanceGhost(items, creditAllowanceRefusal, projectId)
          : items,
      [creditAllowanceRefusal, items, projectId]
    );
  },
  useIsWorkSessionArtifactV2Enabled: () => useIsWorkSessionArtifactV2Enabled(),
  useProjectArtifactAutoOpen: (args) => useProjectArtifactAutoOpen(args),
  useIngestComputerUseNotePushCandidates: (args) =>
    useIngestComputerUseNotePushCandidates(args),
  usePublishNotePushApply: (params) => usePublishNotePushApply(params),
  createdArtifactPorts: CREATED_ARTIFACT_PORTS,
  registerProjectBackground,
  unregisterProjectBackground,
  invalidateSessionsList,
  trackProjectChatErrorOnce,
  trackWorkExperienceFeedbackActioned,
  trackWorkRpaDesktopRequiredShown,
  trackMobileHandoffCardActioned,
  useOpenGetMobileApp: () => {
    const setIsGetMobileAppModalOpen = useSetAtom(isGetMobileAppModalOpenAtom);
    return useCallback(
      () => setIsGetMobileAppModalOpen(true),
      [setIsGetMobileAppModalOpen]
    );
  },
  isProductFeedbackAvailable: () => isProductFeedbackAvailable,
  rememberCreditAllowanceRefusal,
  isDesktopAvailable: () => rpaBridge.isAvailable(),
  isDevelopmentBuild: process.env.NODE_ENV === 'development',
  residentDrawer: RESIDENT_DRAWER_HOST,
  ProjectChatComposer,
  ProjectConversationTimeline,
  ProjectReplyMessageActions,
  ProjectMessageActions,
  AssistantText,
  ExperienceFeedbackNudge,
};

/** The live turn's stop affordance, mirrored up to an embedding host. */
export type ProjectChatTurnControls = ProjectChatViewTurnControls;

/**
 * `bound` — this chat now has a project and finishes the turn itself; `consumed`
 * — the host took the turn elsewhere; `refused` — the draft returns silently.
 */
export type ProvisionalSubmitOutcome = ProvisionalSubmitViewOutcome;

export type ProjectChatProps = ProjectChatViewProps;

/**
 * The Work chat, bound to the app: the transport, the credit allowance chrome,
 * the flags, the routine read, the desktop bridge and permission gate, the
 * patient and session reads, the copilot context, the analytics emits, the
 * cloud resident, and the composer, timeline, reply rows, assistant prose and
 * feedback nudge it renders.
 */
export const ProjectChat = (props: ProjectChatProps) => {
  // Do not add a provider when one already exists above. The Work route
  // provides above this chat, because the picker on the home page and the
  // carry into the created project both live outside this subtree. A second
  // provider here would shadow that one, and the send path would read null.
  // Embedded hosts (session panel, routine panes) have no provider, so they
  // get a per-project fallback.
  const inheritedSelection = useDesktopTargetViewSelection();
  const content = <ProjectChatContent {...props} host={PROJECT_CHAT_HOST} />;
  if (inheritedSelection) return content;
  return (
    <DesktopTargetViewSelectionProvider scope={props.projectId ?? 'new-project'}>
      {content}
    </DesktopTargetViewSelectionProvider>
  );
};
